import { useEffect } from "react";
import { Trash2 } from "lucide-react";

/** Styled replacement for window.confirm, used for destructive actions. */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = "Delete",
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/30" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div
        className="w-[400px] max-w-[92vw] rounded-lg border border-stroke bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-label={title}
      >
        <div className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-red-50 text-red-600">
            <Trash2 size={17} />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold">{title}</h2>
            <p className="pt-1 text-[13px] text-ink-muted">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button
            onClick={onCancel}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            autoFocus
            className="rounded bg-red-600 px-4 py-1.5 text-[13px] font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? "Deleting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
